import React, { useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

interface ChartData {
  timestamp: number;
  client_count: number;
}

interface RendererChartData {
  rendererId: number;
  rendererName: string;
  rendererGroup: string;
  data: ChartData[];
}

interface ConnectionChartProps {
  renderersData: RendererChartData[];
  selectedRendererIds: number[];
  selectedRendererGroups: string[];
}

// 색상 팔레트 (여러 renderer를 구분하기 위해)
const colorPalette = [
  { border: 'rgb(75, 192, 192)', background: 'rgba(75, 192, 192, 0.2)' },
  { border: 'rgb(255, 99, 132)', background: 'rgba(255, 99, 132, 0.2)' },
  { border: 'rgb(54, 162, 235)', background: 'rgba(54, 162, 235, 0.2)' },
  { border: 'rgb(255, 206, 86)', background: 'rgba(255, 206, 86, 0.2)' },
  { border: 'rgb(153, 102, 255)', background: 'rgba(153, 102, 255, 0.2)' },
  { border: 'rgb(255, 159, 64)', background: 'rgba(255, 159, 64, 0.2)' },
  { border: 'rgb(199, 199, 199)', background: 'rgba(199, 199, 199, 0.2)' },
  { border: 'rgb(83, 102, 255)', background: 'rgba(83, 102, 255, 0.2)' },
  { border: 'rgb(255, 99, 255)', background: 'rgba(255, 99, 255, 0.2)' },
  { border: 'rgb(99, 255, 132)', background: 'rgba(99, 255, 132, 0.2)' },
];

const ConnectionChart: React.FC<ConnectionChartProps> = ({ 
  renderersData, 
  selectedRendererIds, 
  selectedRendererGroups 
}) => {
  const chartRef = useRef<ChartJS<'line'>>(null);

  // 필터에 선택된 renderer만 필터링
  const visibleRenderersData = renderersData.filter(rendererData => {
    // 필터가 없으면 모두 표시
    if (selectedRendererIds.length === 0 && selectedRendererGroups.length === 0) {
      return true;
    }

    // Renderer ID 필터 체크
    const matchesId = selectedRendererIds.length > 0 && 
      selectedRendererIds.includes(rendererData.rendererId);
    
    // Renderer Group 필터 체크
    const matchesGroup = selectedRendererGroups.length > 0 && 
      selectedRendererGroups.includes(rendererData.rendererGroup);
    
    // OR 관계: 둘 중 하나라도 만족하면 표시
    return matchesId || matchesGroup;
  });

  // 모든 타임스탬프를 수집하고 정렬 (표시되는 renderer의 데이터만)
  const allTimestamps = Array.from(
    new Set(
      visibleRenderersData.flatMap(rd => rd.data.map(d => d.timestamp))
    )
  ).sort((a, b) => a - b);

  // 각 renderer별로 데이터셋 생성 (표시되는 renderer만)
  const datasets = visibleRenderersData.map((rendererData, index) => {
    const color = colorPalette[index % colorPalette.length];
    
    // 타임스탬프에 맞춰 데이터 매핑 (없는 경우 null)
    const dataPoints = allTimestamps.map(timestamp => {
      const dataPoint = rendererData.data.find(d => d.timestamp === timestamp);
      return dataPoint ? dataPoint.client_count : null;
    });

    return {
      label: `Renderer ${rendererData.rendererId}${rendererData.rendererName !== '(접속 대기 중)' ? ` (${rendererData.rendererName})` : ''}`,
      data: dataPoints,
      borderColor: color.border,
      backgroundColor: color.background,
      tension: 0.1,
      fill: false,
      spanGaps: true,
    };
  });

  const chartData = {
    labels: allTimestamps.map(ts => new Date(ts)),
    datasets: datasets,
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: 'Renderer별 접속수 추이',
      },
    },
    scales: {
      x: {
        type: 'time' as const,
        time: {
          displayFormats: {
            second: 'HH:mm:ss',
            minute: 'HH:mm',
            hour: 'HH:mm',
          },
        },
        title: {
          display: true,
          text: '시간',
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: '접속수',
        },
      },
    },
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
  };

  return (
    <div className="chart-container">
      <div style={{ height: '400px', width: '100%' }}>
        <Line ref={chartRef} data={chartData} options={options} />
      </div>
    </div>
  );
};

export default ConnectionChart;
